// Local `useGetFormattedEventDateTimeString` — a faithful port of the eb-ui
// (React 16, date-fns-tz **v2**) `event-renderer/src/components/EventCardDate/
// hooks.ts` original.
//
// WHY this exists: the sanctioned `@eventbrite/consumer-search.event-renderer`
// 1.5.2's root barrel statically loads `components/EventCardDate/hooks.js`,
// which does `import { toZonedTime, fromZonedTime } from "date-fns-tz"` — the
// **v3** API. This app pins `date-fns-tz@^2.0.0` (top-level is 2.0.1), so ANY
// value import from the package root fails to bundle (the export doesn't
// exist in v2). `node_modules` / `package.json` are out of scope, so:
//   (a) my card components deep-import the sanctioned PRIMITIVES from their
//       leaf files (bypassing the root `index.js` — see `eventRenderer.ts`),
//       so `EventCardDate/hooks.js` is never bundled; and
//   (b) this local hook replaces the sanctioned `useGetFormattedEventDateTimeString`
//       using the SAME date-fns-tz **v2** primitives the eb-ui original used
//       (`utcToZonedTime`/`zonedTimeToUtc`) + `date-fns` v2 (`differenceInDays`/
//       `formatRelative`) — both present in the workspace.
//
// Copy + behaviour (relative "Today/Tomorrow/…" for <6 days, absolute Intl
// string otherwise, en_US `•` delimiter + short timezone suffix for online
// events, the "X more" series suffix via gettext) matches the eb-ui original.
import { differenceInDays, formatRelative } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, enGB } from 'date-fns/locale';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import React, { useEffect, useMemo, useState } from 'react';

import { gettext } from '@/lib/gettext';

function getDateFnsLocale(locale: string): Locale {
    // The sanctioned helper maps a handful of en-* locales to enGB/enUS; this
    // app runs en (the locale string is en_US / a dash form), so enUS is the
    // faithful default. Non-en locales degrade to enUS (documented).
    const lower = (locale || 'en_US').toLowerCase();
    return lower.startsWith('en-gb') || lower.startsWith('en_gb') ? enGB : enUS;
}

function guessUserTimezone(): string {
    if (typeof window === 'undefined' || typeof Intl === 'undefined') {
        return '';
    }
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return '';
    }
}

interface UseUserTimezoneConfig {
    defaultTimezone: string;
    enabled?: boolean;
}

const useGuessUsersTimezone = (config: UseUserTimezoneConfig): string => {
    const { enabled, defaultTimezone } = config;
    const [userTimezone, setUserTimezone] = useState(defaultTimezone || '');
    useEffect(() => {
        if (enabled) {
            const timezone = guessUserTimezone();
            if (typeof timezone === 'string') {
                setUserTimezone(timezone);
            }
        }
    }, [enabled]);
    return enabled ? userTimezone : defaultTimezone;
};

function getFormattedDateString(
    eventDate: Date,
    currentDate: Date,
    locale: Locale,
): string {
    const dateDelta = differenceInDays(eventDate, currentDate);
    if (dateDelta < 0) {
        return getFormattedPastDateTimeString(eventDate, locale);
    }
    const shouldUseRelativeDate = dateDelta < 6;
    if (shouldUseRelativeDate) {
        const relativeDateString = formatRelative(eventDate, currentDate, {
            locale,
        });
        if (relativeDateString.startsWith('today')) {
            return relativeDateString.replace('today', 'Today');
        }
        if (relativeDateString.startsWith('tomorrow')) {
            return relativeDateString.replace('tomorrow', 'Tomorrow');
        }
        return relativeDateString;
    }
    return new Intl.DateTimeFormat(locale.code, {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
    }).format(eventDate);
}

function getFormattedPastDateTimeString(eventDate: Date, locale: Locale): string {
    return new Intl.DateTimeFormat(locale.code, {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
    }).format(eventDate);
}

function getEventDateObjects(
    dateTimeString: string,
    eventTimezone: string,
    localTimezone: string,
    shouldLocalize: boolean,
): Date[] {
    if (shouldLocalize) {
        const resetEventDate = zonedTimeToUtc(
            new Date(dateTimeString),
            eventTimezone,
        );
        const localizedEventDate = utcToZonedTime(resetEventDate, localTimezone);
        return [localizedEventDate, utcToZonedTime(new Date(), localTimezone)];
    }
    const eventDate = new Date(dateTimeString);
    const currentDate = new Date();
    return [eventDate, currentDate];
}

/**
 * Replace the last comma in an absolute date, or the ' at ' in a relative
 * date, with the `dateTimeDelimeter` (en_US only).
 */
export function getFormattedDateStringWithDelimeter(
    formattedDateString: string,
    dateTimeDelimeter: string,
): string {
    if (formattedDateString.includes(' at ')) {
        return formattedDateString.replace(' at ', ` ${dateTimeDelimeter} `);
    }
    const calendarDateIndexOf = formattedDateString.lastIndexOf(',');
    return `${formattedDateString.substring(
        0,
        calendarDateIndexOf,
    )} ${dateTimeDelimeter} ${formattedDateString.substring(
        calendarDateIndexOf + 1,
    )} `;
}

export interface GetFormattedEventDateString {
    startDate?: string;
    startTime: string;
    timezone: string;
    locale: string;
    repeatingInstanceCount?: number;
    useUserTimezone?: boolean;
    dateTimeDelimeter?: string;
}

/**
 * Local replacement for the sanctioned `useGetFormattedEventDateTimeString`
 * (see the module header for why). Same input shape (the eb-ui original
 * accepts `repeatingInstanceCount`; the sanctioned v1.5.2 swapped it for
 * `additionalSessionsCount` — the card call-sites pass the repeating count,
 * so this keeps the eb-ui/original contract).
 */
export function useGetFormattedEventDateTimeString(
    config: GetFormattedEventDateString,
): string | undefined {
    const {
        startTime,
        startDate,
        repeatingInstanceCount,
        useUserTimezone,
        timezone: eventTimezone,
        locale = 'en_US',
        dateTimeDelimeter,
    } = config;

    const timezone = useGuessUsersTimezone({
        enabled: useUserTimezone && !!startDate,
        defaultTimezone: eventTimezone,
    });

    const formattedDate = useMemo(() => {
        if (!startDate) {
            return undefined;
        }
        try {
            const dateFnsLocale = getDateFnsLocale(locale);
            const [eventDate, currentDate] = getEventDateObjects(
                `${startDate}T${startTime}`,
                eventTimezone,
                timezone,
                !!useUserTimezone,
            );
            let formattedDateString = getFormattedDateString(
                eventDate,
                currentDate,
                dateFnsLocale,
            );
            if (dateTimeDelimeter && locale === 'en_US') {
                formattedDateString = getFormattedDateStringWithDelimeter(
                    formattedDateString,
                    dateTimeDelimeter,
                );
            }
            if (useUserTimezone) {
                const timezoneShortName =
                    new Intl.DateTimeFormat(dateFnsLocale.code, {
                        timeZoneName: 'short',
                        timeZone: timezone,
                    })
                        .format(eventDate)
                        .split(' ')[1] || '';
                formattedDateString = `${formattedDateString} ${timezoneShortName}`;
            }
            if (repeatingInstanceCount) {
                return gettext(
                    '%(formattedDateString)s + %(repeatingInstanceCount)s more',
                    { formattedDateString, repeatingInstanceCount },
                ).toString();
            }
            return formattedDateString;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            return undefined;
        }
    }, [
        locale,
        timezone,
        startDate,
        startTime,
        repeatingInstanceCount,
        eventTimezone,
        useUserTimezone,
        dateTimeDelimeter,
    ]);

    return formattedDate;
}
